import React, { useState, useEffect, useMemo } from "react";
import CheckboxGroup from "../CheckboxGroup/CheckboxGroup";
import Button from "../Button/Button";
import ChatTilesContainer from "../ChatTilesContainer/ChatTilesContainer";
import "./Leftbar.css";

interface Chat {
  id: string;
  chatTopic: string;
  chatText: string;
  creationDate: Date;
}

interface SortedChats {
  last7Days: Chat[];
  last30Days: Chat[];
  older: Chat[];
}

interface LeftbarProps {
  hasChat?: boolean;
  hasMultipleCheckbox?: boolean;
  hasCheckbox?: boolean;
  hasDetails?: boolean;
  hasButton?: boolean;
  title: string;
  sourceOptions?: { label: string; value: string }[];
  typeOptions?: { label: string; value: string }[];
  text?: string;
  titleFirstCheckbox?: string;
  titleSecondCheckbox?: string;
  hasTextCTA?: boolean;
  CTAText?: string;
  hasIcon?: boolean;
  iconPath?: string;
  CTAPosition?: "first" | "second" | "both";
  onButtonClick?: () => void;
  chats?: Chat[];
  selectedValues?: string[];
  onSelectionChange?: (selected: string[]) => void;
}

function Leftbar({
  hasButton = false,
  hasChat = false,
  hasMultipleCheckbox = false,
  hasCheckbox = false,
  hasDetails = false,
  title,
  sourceOptions = [],
  typeOptions = [],
  text = "",
  titleFirstCheckbox = "",
  titleSecondCheckbox = "",
  hasTextCTA = false,
  CTAText = "",
  hasIcon = false,
  iconPath = "",
  CTAPosition = "first",
  onButtonClick,
  chats = [],
  selectedValues,
  onSelectionChange,
}: LeftbarProps) {
  const getSevenDaysAgo = () => {
    const today = new Date();
    today.setDate(today.getDate() - 7);
    return today;
  };

  const getThirtyDaysAgo = () => {
    const today = new Date();
    today.setDate(today.getDate() - 30);
    return today;
  };

  const compareDates = (date1: Date, date2: Date) => {
    return date2.getTime() - date1.getTime();
  };

  const sortedChats = useMemo(() => {
    const sevenDaysAgo = getSevenDaysAgo();
    const thirtyDaysAgo = getThirtyDaysAgo();

    const groupedChats: SortedChats = {
      last7Days: [],
      last30Days: [],
      older: [],
    };

    chats.forEach((chat) => {
      const creationDate = new Date(chat.creationDate);
      if (creationDate >= sevenDaysAgo) {
        groupedChats.last7Days.push(chat);
      } else if (creationDate >= thirtyDaysAgo) {
        groupedChats.last30Days.push(chat);
      } else {
        groupedChats.older.push(chat);
      }
    });

    Object.keys(groupedChats).forEach((key) => {
      groupedChats[key as keyof SortedChats].sort((a, b) =>
        compareDates(a.creationDate, b.creationDate)
      );
    });

    return groupedChats;
  }, [chats]);

  return (
    <aside className="leftbar">
      <div className="leftbar-upper">
        <h2>{title}</h2>
        {hasButton && <Button onClick={onButtonClick} text={text}></Button>}
      </div>

      {hasDetails && (
        <div className="details-section">
          <div className="documents-report">
            <h2>10</h2>
            <p>Documents scanned</p>
          </div>
          <div className="issues-report">
            <h2>0</h2>
            <p>Issues detected</p>
          </div>
        </div>
      )}

      {hasMultipleCheckbox && CTAPosition === "both" && (
        <>
          <CheckboxGroup
            title={titleFirstCheckbox}
            options={sourceOptions}
            selectedValues={selectedValues}
            hasTextCTA={hasTextCTA}
            CTAText={CTAText}
            hasIcon={hasIcon}
            iconPath={iconPath}
            onSelectionChange={onSelectionChange}
          />
          <CheckboxGroup
            title={titleSecondCheckbox}
            options={typeOptions}
            selectedValues={selectedValues}
            hasTextCTA={hasTextCTA}
            CTAText={CTAText}
            hasIcon={hasIcon}
            iconPath={iconPath}
            onSelectionChange={onSelectionChange}
          />
        </>
      )}

      {hasMultipleCheckbox && CTAPosition === "first" && (
        <>
          <CheckboxGroup
            title={titleFirstCheckbox}
            options={sourceOptions}
            selectedValues={selectedValues}
            hasTextCTA={hasTextCTA}
            CTAText={CTAText}
            hasIcon={hasIcon}
            iconPath={iconPath}
            onSelectionChange={onSelectionChange}
          />
          <CheckboxGroup
            title={titleSecondCheckbox}
            options={typeOptions}
            selectedValues={selectedValues}
            onSelectionChange={onSelectionChange}
          />
        </>
      )}

      {hasMultipleCheckbox && CTAPosition === "second" && (
        <>
          <CheckboxGroup
            title={titleFirstCheckbox}
            options={sourceOptions}
            selectedValues={selectedValues}
            onSelectionChange={onSelectionChange}
          />
          <CheckboxGroup
            title={titleSecondCheckbox}
            options={typeOptions}
            selectedValues={selectedValues}
            hasTextCTA={hasTextCTA}
            CTAText={CTAText}
            hasIcon={hasIcon}
            iconPath={iconPath}
            onSelectionChange={onSelectionChange}
          />
        </>
      )}

      {hasCheckbox && (
        <CheckboxGroup
          title={titleFirstCheckbox}
          options={sourceOptions}
          selectedValues={selectedValues}
          hasTextCTA={hasTextCTA}
          CTAText={CTAText}
          hasIcon={hasIcon}
          iconPath={iconPath}
          onSelectionChange={onSelectionChange}
        />
      )}

      {hasChat && (
        <div className="chats-section">
          {sortedChats.last7Days.length > 0 && (
            <div>
              <h3>Last 7 Days</h3>
              <ChatTilesContainer chats={sortedChats.last7Days} />
            </div>
          )}

          {sortedChats.last30Days.length > 0 && (
            <div>
              <h3>Last 30 Days</h3>
              <ChatTilesContainer chats={sortedChats.last30Days} />
            </div>
          )}

          {sortedChats.older.length > 0 && (
            <div>
              <h3>Older Chats</h3>
              <ChatTilesContainer chats={sortedChats.older} />
            </div>
          )}
        </div>
      )}
    </aside>
  );
}

export default Leftbar;
